import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import {ApiService} from '@modules/api-service';
import {groupTypes, pages, sdoNs} from '@modules/constants-enumerate';
import {storeInstance} from '@aofl/store/dist/legacy';
import {uuid} from '@aofl/uuid';
import './sdo';
import translations from '@modules/i18n';

/**
 * @summary GroupFiltersMenu
 * @extends {AoflElement}
 */
@customElement('group-filters-menu')
class GroupFiltersMenu extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of GroupFiltersMenu.
   */
  constructor() {
    super();
    this.storeInstance = storeInstance;
    this._resetSearchQuery();
    this.limit = 10;
    this.translations = translations;
  }
  /**
   * @readonly
   */
  static is = 'group-filters-menu';

  @property({type: Boolean, attribute: true})
  ['show-reset'] = false;

  @property({type: Array})
  ['selected-groups'] = [];

  @property({type: Array, attribute: false, mapState: `${sdoNs.SESSION}.settings.tier_structure.labels`, store: storeInstance})
  tierStructureLabels = [];

  @property({type: Object, mapState: `${sdoNs.SESSION}.group_info`, store: storeInstance})
  adminGroup = {};

  @property({type: Number, attribute: false, mapState: `${sdoNs.GROUP_FILTERS_MENU}.$levelBelowAdminOrg`, store: storeInstance})
  levelBelowAdminOrg = 2;

  @property({type: Boolean, attribute: false, mapState: `${sdoNs.GROUP_FILTERS_MENU}.loading`, store: storeInstance})
  loading = true;

  @property({type: Number, mapState: `${sdoNs.SESSION}.$classLevel`, store: storeInstance})
  classLevel = null;

  @property({type: String, mapState: `${sdoNs.DASHBOARD}.pendingFilters.stats_path`, store: storeInstance})
  statsPath;

  @property({type: Object})
  groupsAtLevel = {};

  @property({type: Object})
  groupsAtTopLevel = {};

  @property({type: Object})
  selectedGroupsInfo = {};

  @property({type: String})
  rndId = uuid();

  @property({type: Number, attribute: true})
  ['max-level'] = null;

  @property({type: Boolean})
  ['full-width'] = false;

  @property({type: Boolean})
  scrollIntoView = false;

  @property({type: Boolean})
  showThirdPartyConnected = true;

  @property({type: String})
  page = pages.DASHBOARD;

  connectedCallback() {
    super.connectedCallback();
    this.getGroupInfo();
  }

  async getGroupInfo() {
    // if there are existing selections in group filters
    if (this['selected-groups'].length) {
      const lastGroupId = [...this['selected-groups']].pop();
      const selectedGroups = this['selected-groups'].map(id => parseInt(id, 10));
      const response = await ApiService.groupGet(selectedGroups, ['id', 'name', 'path', 'type', 'teachers']);
      this.selectedGroupsInfo = response.results;
      const lastGroupInfo = response.results[lastGroupId];
      storeInstance.commit({
        namespace: sdoNs.SESSION,
        mutationId: 'setSelectedGroup',
        payload: lastGroupInfo
      });
      this.searchGroups(lastGroupInfo.path, lastGroupInfo.level);
    }
  }

  getSiblings(groupId, dropdownIndex, doReset = true) {
    if (doReset) {
      this._resetSearchQuery();
    }
    // added the one because levels start at one because dropdown counts start
    // at zero and org level index starts at 1;
    let path = '';
    if (groupId) {
      const groupInfo = this.selectedGroupsInfo[groupId];
      path = groupInfo.path;
    } else {
      const previouslySelectedGroupId = this['selected-groups'][dropdownIndex - 1] || null;
      if (previouslySelectedGroupId) {
        path = this.selectedGroupsInfo[previouslySelectedGroupId].path + this.selectedGroupsInfo[previouslySelectedGroupId].id + '.';
      }
    }
    const level = this.adminGroup.level + dropdownIndex + 1;
    this.searchGroups(
      path,
      level
    );
  }

  async searchGroups(path, level) {
    this.storeInstance.commit({namespace: sdoNs.GROUP_FILTERS_MENU, mutationId: 'setLoading', payload: true});
    const search = (this.search?.query) ? this.search : void 0;

    let groupType = (level === this.classLevel) ? groupTypes.CLASSROOM : groupTypes.MANAGEMENT;
    const resp = await ApiService.groupSearch(
      search,
      {
        path,
        level,
        type: groupType
      },
      {
        fields: ['id', 'name'],
        direction: 'asc',
        limit: this.limit,
      },
      this.showThirdPartyConnected
    );

    this.groupsAtLevel = {
      ...this.groupsAtLevel,
      [level]: resp
    };
    this.storeInstance.commit({namespace: sdoNs.GROUP_FILTERS_MENU, mutationId: 'setLoading', payload: false});
  }

  triggerSearch(e, groupId, dropdownIndex) {
    this.search = e.detail;
    this.getSiblings(groupId, dropdownIndex, false);
  }

  triggerTopOrgSearch(e) {
    this.search = e.detail;
    this.getSiblings(null, 1, false);
  }

  toggleDropdown(groupId, dropdownIndex) {
    const state = storeInstance.getState();
    const dropDownBoxOpened = state[sdoNs.DROPDOWN]['group-dropdown-' + this.rndId + '-' + dropdownIndex];
    if (dropDownBoxOpened) {
      this.getSiblings(groupId, dropdownIndex);
      setTimeout(() => {
        this.shadowRoot.getElementById(`obj-search-form-${dropdownIndex}`)
          ?.focusSearch();
        if (this.scrollIntoView) {
          this.shadowRoot.getElementById(`obj-dropdown-${dropdownIndex}`)
            ?.scrollIntoView({
              block: 'center',
              behavior: 'smooth'
            });
        }
      }, 210);
    }
  }

  selectGroup(groupInfo) {
    // this['selected-groups'] was added for the
    // base-search-org-filter compatibility
    const statsPath = groupInfo.path.split('.');
    statsPath.pop();
    statsPath.push(groupInfo.id);
    if (statsPath.length > 0) {
      this['selected-groups'] = statsPath.slice(this.adminGroup.level);
    }
    // everything below this is not for the base-search-org
    this.selectedGroupsInfo[groupInfo.id] = groupInfo;

    const url = new URL(location.href);
    const productInUrl = decodeURIComponent(url.searchParams.get('product'));
    url.searchParams.set('filter.stats_path[], product', [, productInUrl]);

    this.dispatchEvent(new CustomEvent('group-change', {
      detail: {
        groupInfo
      },
      composed: true,
      bubbles: true
    }));
    this.storeInstance.commit({
      namespace: sdoNs.DROPDOWN,
      mutationId: 'closeAll'
    });
    this._resetSearchQuery();
  }

  _resetSearchQuery() {
    this.search = {
      query: '',
      field: 'name'
    };
  }

  resetFilter() {
    // this['selected-groups'] was added for the
    // base-search-org-filter compatibility
    this['selected-groups'] = [];
    this.checkedGroupCheckboxes = [];

    storeInstance.commit({
      namespace: sdoNs.SESSION,
      mutationId: 'setSelectedGroup',
      payload: this.adminGroup
    });
    this.dispatchEvent(new CustomEvent('group-change', {
      composed: true,
      bubbles: true
    }));

    // Force a page reload because:
    //    When the dashboard is on http://localhost:9004/dashboard/ and you hit the group-filter reset, the page
    //    does not reload and rerun the constructor to get a value for iSInternalDashboard value because the URL
    //    does not change.
    //    When you are on the MRA tab, the URL is http://localhost:9004/dashboard/?product=my_reading_academy and a
    //    Reset filter there does change the URL and reload.
    //    The reload also works when hitting reset when you are on http://localhost:9004/dashboard/?product=mathmastery
    window.location = this.page;
  }

  update(changedProperties) {
    if (changedProperties.has('statsPath')) {
      this.getGroupInfo();
    }
    super.update(changedProperties);
  }

  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {template, styles: [styles]}
    });
  }
}

export default GroupFiltersMenu;
